
const months = [9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7, 8]
const dayInMonths = {0: 31, 1: 28, 2: 31, 3: 30, 4: 31, 5: 30, 6: 31, 7: 31, 8: 30, 9: 31, 10: 30, 11: 31}

export const createDayOfMonthLabels = (withLeapYears) => {
    return months.flatMap(month => {
        const days = Array.from(
            {length: dayInMonths[month] + (month === 1 && withLeapYears ? 1 : 0)},
            (_, i) => i + 1
        )
        return days.map(day => `${day}/${month + 1}`)
    })
}

export const isLeapYear = (year) => {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

const defaultTrimFn = (x => x === null || x === undefined)

// trim and array of labels based on the object data.
// The labels are trimmed from the start and the end of the array
// if the associated data is null or undefined.
export const trimLabels = (labels, data, trimFn=defaultTrimFn) => {
    let start = 0;
    let end = labels.length - 1;

    // Find the first non-null/non-undefined index
    while (start <= end && (trimFn(data[labels[start]]) || trimFn(data[labels[start]]))) {
        start++;
    }

    // Find the last non-null/non-undefined index
    while (end >= start && (trimFn(data[labels[end]]) || trimFn(data[labels[end]]))) {
        end--;
    }

    // Return the trimmed array
    return labels.slice(start, end + 1);
}

// given an object data, an array of attributes and a key to group by,
// generate statistics for each attribute grouped by the key.
export const generateStatisticsByKey = (data, attributes, groupByKey, attributeDataLength=null) => {
    const aggregatedData = groupBy(data, groupByKey);

    return Object.entries(aggregatedData).reduce((acc, [key, values]) => {
        acc[key] = generateStatistics(values, attributes, attributeDataLength);
        return acc
    }, {})
}

export const generateStatistics = (data, attributes, attributeDataLength=null) => {
    return attributes.reduce((acc, attribute) => {
        const attributeValues = data.map(d => d[attribute]);
        const meanDenominator = attributeDataLength ?? attributeValues.length;  // use provided length or actual length. Provided length can be used to account for missing data.
        const mean = meanDenominator > 0 ? attributeValues.reduce((acc, value) => acc + value, 0) / meanDenominator : 0;
        const min = Math.min(...attributeValues)
        const max = Math.max(...attributeValues)
        acc[attribute] = {mean, min, max}
        return acc
    }, {})
}

export const generateSumByKey = (data, attributes, groupByKey) => {
    const aggregatedData = groupBy(data, groupByKey);

    return Object.entries(aggregatedData).reduce((acc, [key, values]) => {
        acc[key] = generateSum(values, attributes);
        return acc
    }, {})
}

export const generateCountByKey = (data, groupByKey) => {
    const aggregatedData = groupBy(data, groupByKey);

    return Object.entries(aggregatedData).reduce((acc, [key, values]) => {
        acc[key] = values.length;
        return acc
    }, {})
}

export const generateSum = (data, attributes) => {
    return attributes.reduce((acc, attribute) => {
        const sum = data.reduce((acc, d) => acc + d[attribute], 0);
        acc[attribute] = {sum}
        return acc
    }, {})
}

const defaultAttrGetter = (d, x) => d?.[x] ?? 0

export const generateCumulative = (xValues, data, attrGetter=defaultAttrGetter) => {
    let cum = 0;
    const acc = {};
    for (const x of xValues){
        cum += attrGetter(data, x)
        acc[x] = cum
    }
    return acc
}

// group an array of objects by a key
export const groupBy = (data, key) => {
    return data.reduce((acc, d) => {
        if (!acc[d[key]]) {
            acc[d[key]] = []
        }
        acc[d[key]].push(d)
        return acc
    }, {})

}

// partition an array of objects into two arrays based on the
// season attribute of data objects.
export const partitionData = (selectedSeason, historyYearFrom, historyYearTo, data) => {
    const [selected, history] = data.reduce((acc, d) => {
        if (d.season === selectedSeason) {
            acc[0].push(d)
        }
        if (d.season >= historyYearFrom && d.season <= historyYearTo) {
            acc[1].push(d)
        }
        return acc
    }, [[], []]);
    return [selected, history]
}


export const extractSeason = (data, season) => {
    return data.filter(d => d.season === season)
}

// given a date create a label with the format dd/mm
export const getDayOfYearLabel = (date) => {
    return `${date.getDate()}/${date.getMonth() + 1}`
}

// Given a date return the season year. The season start on 1st October
// and ends on 30th September.
// The season year is the year of the season start date.
export const getSeason = (date) => {
    return date.getMonth() < 9 ? date.getFullYear() - 1 : date.getFullYear()
}

export const getSeasonLabel = (season) => {
    if (season === null || season === undefined) {
        return "--"
    }
    return `${season}/${season + 1}`
}

export const extractAvailableSeasons = (data) => {
    const seasons = Array.from(new Set(data.map(d => d.season)));
    return seasons.sort((a, b) => a - b);
}

export const objectify = (data, key) => {
    return data.reduce((acc, d) => {
        acc[d[key]] = d
        return acc
    }, {})
}

export const getRegistryObject = (registry, registryName) => {
    return (registry?.[registryName] || []).reduce((acc, curr) => {
        acc[curr.code] = curr.description;
        return acc;
    }, {});
}